<script setup lang="ts">
import type { ShortsSectionVariant } from './variants';
import type { ShortVideo } from '~/composables/shorts';
import type {
	FeaturedVideosRecommendationType,
	ShortsFilters
} from '~/core/entities/property/filters';
import type { SectionName } from '~/types/mixpanel';

import { cn, useStaleGuard } from '@/utils';
import {
	breakpointsTailwind,
	useBreakpoints,
	useEventListener,
	useIntersectionObserver
} from '@vueuse/core';
import { useEvents } from '~/composables/events/useEvents';
import { useFetchShorts } from '~/composables/shorts';
import { useOpenShortsFeed } from '~/composables/shorts/useOpenShortsFeed';
import { SHORTS_HOME_PATH } from '~/composables/useShortsFilters/buildShortsLocationPath';
import {
	hasRecommendationReference,
	withImagesEvery
} from '~/composables/useShortsFilters/featuredVideosFilterRules';
import {
	prefetchVideoOnIntent,
	useCarouselSegmentWarmup,
	warmVideoManifests
} from '~/composables/useVideoWarmup';
import { MixpanelProps } from '~/core/service/mixpanel';
import { hash } from 'ohash';

import { VideoCard } from '~/components/VideoCard';

import IconMaterialSymbolsSlowMotionVideo from '~icons/material-symbols/slow-motion-video-sharp';

import SectionSkeleton from './SectionSkeleton.vue';
import { getSectionStyles } from './variants';

const DEFAULT_MAX_VIDEOS = 15;
// Página maior só no fluxo de entrada da SRP (`preserveFeedEntryContext`): com
// `includeImagesEvery`, o `per_page` do Heimdall conta o total de itens finais
// (vídeos + imagens), então 17 mantém ~15 vídeos visíveis após esconder as imagens.
const FEED_ENTRY_PER_PAGE = 17;

const props = withDefaults(
	defineProps<{
		title: string;
		filters?: ShortsFilters;
		variant?: ShortsSectionVariant;
		showTitleIcon?: boolean;
		singleCardOnLg?: boolean;
		disablePeekFromLg?: boolean;
		useCompactMobileTopSpacing?: boolean;
		useParentMobileGutter?: boolean;
		hideBottomBorder?: boolean;
		enableRecommendationFallback?: boolean;
		enableRecommendationTypeAnalytics?: boolean;
		preserveFeedEntryContext?: boolean;
		titleSource?: 'prop' | 'api';
		sliderSectionName?: SectionName;
		videoCardSectionName?: SectionName;
		ctaSectionName?: SectionName;
		videoCardPositionMode?: 'absolute' | 'visible';
		fetchPage?: number;
		fetchPerPage?: number;
		maxVideos?: number;
	}>(),
	{
		variant: 'dark',
		showTitleIcon: true,
		singleCardOnLg: false,
		disablePeekFromLg: false,
		useCompactMobileTopSpacing: false,
		useParentMobileGutter: false,
		hideBottomBorder: false,
		enableRecommendationFallback: false,
		enableRecommendationTypeAnalytics: false,
		preserveFeedEntryContext: false,
		titleSource: 'prop',
		sliderSectionName: 'shorts',
		videoCardSectionName: 'shorts hero section',
		ctaSectionName: 'shorts',
		videoCardPositionMode: 'absolute',
		fetchPage: 1,
		maxVideos: DEFAULT_MAX_VIDEOS
	}
);

const styles = computed(() => getSectionStyles(props.variant));
const sectionClass = computed(() =>
	cn(styles.value.section, props.hideBottomBorder && 'border-b-0')
);
const containerClass = computed(() =>
	cn(styles.value.containerClass, props.useCompactMobileTopSpacing && 'pt-4')
);
const carouselItemClass = computed(() =>
	cn(
		styles.value.carouselItemClass,
		props.disablePeekFromLg &&
			'lg:basis-[calc(50%-9px)] xl:basis-[calc(33.33333%-12px)]',
		props.singleCardOnLg && 'lg:basis-full'
	)
);
const usesParentMobileGutter = computed(
	() => props.variant === 'light' && props.useParentMobileGutter
);
const headerClass = computed(() =>
	cn(styles.value.headerClass, usesParentMobileGutter.value && 'px-0')
);
const footerClass = computed(() =>
	cn(styles.value.footerClass, usesParentMobileGutter.value && 'px-0')
);

const carouselOptions = computed(() => ({
	cyclic: true,
	class: styles.value.contentGap,
	carouselWrapperClasses: cn(
		styles.value.carouselWrapperClass,
		usesParentMobileGutter.value && 'px-0'
	)
}));

const emit = defineEmits<{
	(e: 'update:has-videos', value: boolean): void;
	(e: 'update:used-fallback', value: boolean): void;
}>();

const { fetchVideos } = useFetchShorts();

const allVideos = ref<ShortVideo[]>([]);
const isLoading = ref(false);
const isPreloaded = ref(false);
const feedHasMore = ref(false);
const appliedFeedFilters = ref<ShortsFilters | undefined>(undefined);
const apiSectionTitle = ref<string | undefined>(undefined);
const recommendationType = ref<FeaturedVideosRecommendationType | undefined>(
	undefined
);

const videos = computed(() =>
	(allVideos.value || [])
		.filter(
			(item): item is ShortVideo & { videoUrl: string } => item.videoUrl != null
		)
		.slice(0, props.maxVideos)
);

const isMobileOrTablet = ref(false);
const sectionRef = ref<HTMLElement | null>(null);
const isSectionVisible = ref(false);

// While the carousel is on screen and the network is provably fast, lazily
// pull the first cards' opening segments in idle time — a tap then starts
// playback from cache. All gating lives in the composable.
useCarouselSegmentWarmup(
	() => videos.value.map(item => item.videoUrl),
	() => isSectionVisible.value
);

// Posters além dos 3 primeiros são lazy nativos — na rolagem rápida o usuário
// alcança cards ainda baixando. Quando a seção se APROXIMA do viewport
// (margem de ~1000px), os primeiros NEAR_POSTER_EAGER_COUNT viram eager e
// chegam prontos. O observer só arma após a primeira interação real
// (scroll/toque/ponteiro): o Lighthouse nunca interage, então o carregamento
// inicial da página fica intocado por construção.
const NEAR_POSTER_EAGER_COUNT = 8;
const isSectionNear = ref(false);
const posterEagerCount = computed(() =>
	isSectionNear.value ? NEAR_POSTER_EAGER_COUNT : undefined
);

function armPosterPreloadOnApproach(): void {
	// O observer nasce dentro do callback do evento — fora do escopo do
	// componente, onde o VueUse NÃO consegue registrar a limpeza automática.
	// Sem este onUnmounted, navegar para outra página entre a 1ª interação e a
	// aproximação da seção reteria o observer (e o subtree observado) para
	// sempre.
	let stopNearObserver: (() => void) | null = null;
	onUnmounted(() => stopNearObserver?.());

	// `once` remove cada listener após o próprio disparo; os `stop` limpam os
	// irmãos que não dispararam — e o useEventListener amarra tudo ao escopo do
	// componente, então um unmount antes da 1ª interação não vaza listener.
	const stops = (['scroll', 'pointermove', 'touchstart'] as const).map(
		eventName =>
			useEventListener(
				window,
				eventName,
				() => {
					stops.forEach(stop => stop());
					if (!sectionRef.value) return;
					const { stop: stopObserver } = useIntersectionObserver(
						sectionRef,
						entries => {
							if (!entries[0]?.isIntersecting) return;
							isSectionNear.value = true;
							stopObserver();
						},
						{ rootMargin: '1000px 0px', threshold: 0 }
					);
					stopNearObserver = stopObserver;
				},
				{ passive: true, once: true }
			)
	);
}

const sliderRef = ref<{
	currentCarouselItemIndex: number;
	scrollTo: (index: number) => void;
} | null>(null);

const shouldAutoplay = computed(
	() => isMobileOrTablet.value && isSectionVisible.value
);

// Swipe-ahead (autoplay mobile): ao trocar o card ativo, aquece a cadeia do
// PRÓXIMO — o swipe seguinte encontra manifesto + 1º segmento no cache e o
// autoplay começa sem a espera visível do download. O card recém-ativo já
// baixa os próprios bytes pelo player; quem precisa de antecipação é o vizinho.
watch(
	() => sliderRef.value?.currentCarouselItemIndex,
	newIndex => {
		if (!shouldAutoplay.value || newIndex == null) return;
		const next = videos.value[newIndex + 1];
		if (next) prefetchVideoOnIntent(next.videoUrl);
	}
);

const hasVideos = computed(() => videos.value.length > 0);

const shouldShowSection = computed(
	() => !isPreloaded.value || isLoading.value || hasVideos.value
);

const displayedTitle = computed(() =>
	props.titleSource === 'api'
		? (apiSectionTitle.value ?? props.title)
		: props.title
);
const feedEntryPerPage = computed(
	() =>
		props.fetchPerPage ??
		(props.preserveFeedEntryContext ? FEED_ENTRY_PER_PAGE : undefined)
);

const fetchGuard = useStaleGuard();

async function loadVideos(filters: ShortsFilters): Promise<void> {
	const capturedId = fetchGuard.next();
	isLoading.value = true;
	// SRP (`preserveFeedEntryContext`): busca a lista completa intercalada (vídeos +
	// imagens) com página maior, para semear o feed. Demais usos mantêm o atual.
	const requestFilters = props.preserveFeedEntryContext
		? withImagesEvery(filters)
		: filters;
	const perPage = feedEntryPerPage.value;
	try {
		const result = await fetchVideos(requestFilters, props.fetchPage, perPage, {
			enableRecommendationFallback: props.enableRecommendationFallback
		});
		if (fetchGuard.isStale(capturedId)) return;
		const appliedFilters = result.appliedFilters ?? requestFilters;
		const usedFallback =
			result.recommendationType === 'fallback' ||
			(props.enableRecommendationFallback &&
				hasRecommendationReference(filters) &&
				Boolean(
					appliedFilters.displayInHomeShorts &&
					!appliedFilters.referenceProperty
				));
		allVideos.value = result.items;
		feedHasMore.value = result.hasMore;
		appliedFeedFilters.value = appliedFilters;
		apiSectionTitle.value = result.sectionTitle?.replace(/\.$/, '');
		recommendationType.value = result.recommendationType;
		isPreloaded.value = true;
		warmVideoManifests(result.items.map(item => item.videoUrl));
		emit('update:used-fallback', usedFallback);
		emit(
			'update:has-videos',
			result.items.some(item => item.videoUrl != null)
		);
	} catch {
		if (fetchGuard.isStale(capturedId)) return;
		allVideos.value = [];
		feedHasMore.value = false;
		apiSectionTitle.value = undefined;
		recommendationType.value = undefined;
		isPreloaded.value = true;
		emit('update:used-fallback', false);
		emit('update:has-videos', false);
	} finally {
		if (!fetchGuard.isStale(capturedId)) {
			isLoading.value = false;
		}
	}
}

onMounted(() => {
	const breakpoints = useBreakpoints(breakpointsTailwind);
	isMobileOrTablet.value = breakpoints.smaller('lg').value;

	watch(breakpoints.smaller('lg'), val => {
		isMobileOrTablet.value = val;
	});

	useIntersectionObserver(
		sectionRef,
		entries => {
			const entry = entries[0];
			if (entry) {
				isSectionVisible.value = entry.isIntersecting;
			}
		},
		{ threshold: 0.3 }
	);

	requestIdleCallback(() => {
		loadVideos(props.filters ?? {});
	});

	armPosterPreloadOnApproach();
});

const fetchParamsSignature = computed(() =>
	hash([props.filters ?? {}, props.fetchPage, props.fetchPerPage])
);

watch(fetchParamsSignature, () => {
	loadVideos(props.filters ?? {});
});

const route = useRoute();
const { trackCtaViewMoreClicked } = useEvents();
const { openShortsFeed } = useOpenShortsFeed();

function handleVideoCardClick(index: number): void {
	const item = videos.value[index];
	if (!item) return;

	// Click = confirmed intent: pull the video's first chunk while the SPA
	// navigation and feed mount happen, so the player finds it locally.
	prefetchVideoOnIntent(item.videoUrl);

	const { commercialId } = item.property;

	if (!props.preserveFeedEntryContext) {
		openShortsFeed({ commercialId, clickedIndex: index });
		return;
	}

	// Semeia o feed com a lista COMPLETA (vídeos + imagens), começando após o vídeo
	// clicado — localizado por `commercialId` na lista bruta `allVideos`, pois a lista
	// visual (`videos`, só-vídeos) diverge quando há imagens antes do clicado. O clicado
	// entra como `target` prependado na página de shorts; imagens posteriores são mantidas.
	const fullIndex = allVideos.value.findIndex(
		video => video.property.commercialId === commercialId
	);
	openShortsFeed({
		commercialId,
		clickedIndex: index,
		entryContext: {
			items: fullIndex >= 0 ? (allVideos.value || []).slice(fullIndex + 1) : [],
			filters: appliedFeedFilters.value ?? props.filters ?? {},
			page: props.fetchPage,
			perPage: feedEntryPerPage.value ?? FEED_ENTRY_PER_PAGE,
			hasMore: feedHasMore.value
		},
		queryFilters: props.filters ?? {}
	});
}

function handleCtaClick(): void {
	trackCtaViewMoreClicked({
		[MixpanelProps.CTA_TEXT]: 'Explorar todos os Shorts',
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.SECTION_NAME]: props.ctaSectionName,
		[MixpanelProps.DESTINATION_URL]: SHORTS_HOME_PATH
	});
	navigateTo(SHORTS_HOME_PATH);
}

function getVideoCardPosition(index: number): number {
	if (props.videoCardPositionMode !== 'visible') {
		return index;
	}

	const currentCarouselItemIndex =
		sliderRef.value?.currentCarouselItemIndex ?? 0;

	return Math.max(1, index - currentCarouselItemIndex + 1);
}
</script>

<template>
	<SectionSkeleton
		v-if="shouldShowSection && !isPreloaded"
		:title="title"
		:variant="variant"
		:hide-bottom-border="props.hideBottomBorder"
	/>

	<section v-else-if="shouldShowSection" ref="sectionRef" :class="sectionClass">
		<StandardSlider
			ref="sliderRef"
			:items="videos"
			:is-loading="false"
			:carousel-item-class="carouselItemClass"
			:carousel-options="carouselOptions"
			:limit-photos="false"
			:section-name="props.sliderSectionName"
			:container-class="containerClass"
			:footer-class="footerClass"
			:carousel-content-class="styles.carouselContentClass"
			:selector-variant="styles.selectorVariant"
			:selector-arrows-variant="styles.selectorArrowsVariant"
			:show-navigation-arrows="false"
		>
			<template #header>
				<header :class="headerClass">
					<div class="flex w-full flex-col items-start justify-between">
						<div v-if="displayedTitle" class="flex items-center gap-3">
							<IconMaterialSymbolsSlowMotionVideo
								v-if="showTitleIcon"
								:class="cn('size-7 md:size-12', styles.titleText)"
							/>
							<h2 :class="cn(styles.titleFont, styles.titleText)">
								{{ displayedTitle }}
							</h2>
						</div>
					</div>
				</header>
			</template>

			<template #items="{ item, index }">
				<VideoCard
					:property="item.property"
					:thumbnail-url="item.thumbnailUrl"
					:video-url="item.videoUrl"
					:index="index"
					:img-eager-count="posterEagerCount"
					:card-position="getVideoCardPosition(index)"
					:section-name="props.videoCardSectionName"
					:recommendation-type="
						props.enableRecommendationTypeAnalytics
							? recommendationType
							: undefined
					"
					:is-active="
						shouldAutoplay && sliderRef?.currentCarouselItemIndex === index
					"
					:hover-to-play="!isMobileOrTablet"
					@click="handleVideoCardClick"
				/>
			</template>

			<template #cta>
				<Button
					v-if="styles.showCtaButton"
					:variant="styles.buttonVariant"
					size="responsive"
					class="text-base font-medium"
					@click="handleCtaClick"
				>
					<span>Explorar todos os Shorts</span>
					<IconMaterialSymbolsLightNorthEast
						:class="cn('size-5 xl:size-6', styles.ctaArrowClass)"
					/>
				</Button>
			</template>
		</StandardSlider>
	</section>
</template>
